import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { io } from 'socket.io-client';

const CartContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

const socket = io(SOCKET_URL); // sin withCredentials

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart debe ser usado dentro de un CartProvider');
    }
    return context;
}

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const [loading, setLoading] = useState(true);

    const fetchCartItems = useCallback(async () => {
        const guestEmail = localStorage.getItem('guest_email');
        if (!guestEmail) {
            setCartItems([]);
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(
                `${API_URL}/cart?guest_email=${encodeURIComponent(guestEmail)}`,
                { 
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            
            if (!response.ok) throw new Error('Error fetching cart');
            const data = await response.json();
            console.log(data)
            // Transformamos los datos para tener la estructura correcta
            const formattedData = data.map(item => {
                // Buscar la imagen principal
                let primaryImage = '/placeholder-image.jpg';
                if (item.products.product_images && Array.isArray(item.products.product_images)) {
                    const primary = item.products.product_images.find(img => img.is_primary) || item.products.product_images[0];
                    if (primary && primary.cloudflare_url) {
                        primaryImage = primary.cloudflare_url;
                    }
                }
                return {
                    id: item.products.id,
                    name: item.products.name,
                    price: item.products.price,
                    stock_quantity: item.products.stock_quantity,
                    primary_image: primaryImage,
                    is_prime: item.products.is_prime,
                    quantity: item.quantity
                };
            });
            setCartItems(formattedData);
        } catch (error) {
            console.error('Error fetching cart:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Efecto para configurar socket y cargar datos iniciales
    useEffect(() => {
        const guestEmail = localStorage.getItem('guest_email');
        if (guestEmail) {
            socket.emit('joinCart', guestEmail);
            fetchCartItems();
        }

        socket.on('cartUpdated', (updatedCart) => {
            setCartItems(Array.isArray(updatedCart) ? updatedCart : []);
        });

        return () => {
            socket.off('cartUpdated');
        };
    }, [fetchCartItems]);

    useEffect(() => {
        setCartCount(cartItems.reduce((total, item) => total + item.quantity, 0));
    }, [cartItems]);

    const addToCart = async (product, quantity = 1, guest_email) => {
        try {
            const response = await fetch(`${API_URL}/cart`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    product_id: product.id,
                    quantity,
                    guest_email
                })
            });
            
            if (!response.ok) throw new Error('Error adding to cart');
            fetchCartItems();
        } catch (error) {
            console.error('Error adding to cart:', error);
        }
    };

    const removeFromCart = async (productId) => {
        const guestEmail = localStorage.getItem('guest_email');
        if (!guestEmail) return;

        try {
            const response = await fetch(
                `${API_URL}/cart?guest_email=${encodeURIComponent(guestEmail)}&product_id=${productId}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            
            if (!response.ok) throw new Error('Error removing from cart');
            await fetchCartItems();
        } catch (error) {
            console.error('Error removing from cart:', error);
        }
    };

    const updateQuantity = async (productId, newQuantity) => {
        const guestEmail = localStorage.getItem('guest_email');
        if (!guestEmail || newQuantity < 1) return;

        try {
            const response = await fetch(`${API_URL}/cart`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    guest_email: guestEmail,
                    product_id: productId,
                    quantity: newQuantity
                })
            });
            
            if (!response.ok) throw new Error('Error updating quantity');
            await fetchCartItems();
        } catch (error) {
            console.error('Error updating quantity:', error);
        }
    };

    const clearCart = async (guest_email) => {
        try {
            const response = await fetch(`${API_URL}/cart?guest_email=${guest_email}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (!response.ok) throw new Error('Error clearing cart');
            fetchCartItems();
        } catch (error) {
            console.error('Error clearing cart:', error);
        }
    };

    const getCartTotal = () => {
        if (!Array.isArray(cartItems)) return 0;
        return cartItems.reduce((total, item) => {
            if (!item || !item.price) return total;
            return total + (parseFloat(item.price) * item.quantity);
        }, 0);
    };

    return (
        <CartContext.Provider value={{
            cartItems,
            cartCount,
            loading,
            addToCart,
            removeFromCart,
            updateQuantity,
            clearCart,
            getCartTotal,
            fetchCartItems
        }}>
            {children}
        </CartContext.Provider>
    );
};